/**
 * DigitizationToolbar Component
 *
 * Toolbar providing access to layer creation, digitization, snapping,
 * and copy/paste operations. Integrates with the LayerCreatorService
 * and Redux state.
 *
 * Validates: Requirements 47.1, 47.6, 48.1, 48.2, 49.1, 49.2
 */
import React, { useCallback, useEffect, useRef } from 'react';
import { Box, IconButton, Tooltip, Divider, Chip, useTheme, alpha } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import StopIcon from '@mui/icons-material/Stop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import GridOnIcon from '@mui/icons-material/GridOn';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  setCreateLayerDialogOpen,
  startDigitization,
  stopDigitization,
  clearPendingGeometry,
  copyToClipboard,
  setSnappingConfigDialogOpen,
  setDigitizationError,
} from '../store/slices/digitizationSlice';
import { createLayerAsync, addFeatureAsync } from '../store/slices/layersSlice';
import { setMeasurementMode } from '../store/slices/measurementSlice';
import { setActiveTool as setDrawingTool } from '../store/slices/drawingSlice';
import { deactivateNodeEditing, deactivateSplitTool } from '../store/slices/geometryEditingSlice';
import { selectSelectedFeatures } from '../store/slices/selectionSlice';
import { layerCreatorService } from '../services/LayerCreatorService';
import { CreateLayerDialog } from './CreateLayerDialog';
import { AttributeInputDialog } from './AttributeInputDialog';
import { SnappingConfigPanel } from './SnappingConfigPanel';
import type {
  CreateLayerConfig,
  ClipboardFeature,
  LayerGeometryType,
} from '../types/layerCreation';
import { getDefaultAttributeFields } from '../types/layerCreation';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const DigitizationToolbar: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();

  const createLayerDialogOpen = useAppSelector((state) => state.digitization.createLayerDialogOpen);
  const attributeInputDialogOpen = useAppSelector(
    (state) => state.digitization.attributeInputDialogOpen
  );
  const mode = useAppSelector((state) => state.digitization.mode);
  const activeGeometryType = useAppSelector((state) => state.digitization.activeGeometryType);
  const activeAttributeFields = useAppSelector((state) => state.digitization.activeAttributeFields);
  const pendingGeometry = useAppSelector((state) => state.digitization.pendingGeometry);
  const activeDigitizationLayerId = useAppSelector(
    (state) => state.digitization.activeDigitizationLayerId
  );
  const clipboard = useAppSelector((state) => state.digitization.clipboard);
  const snapping = useAppSelector((state) => state.digitization.snapping);
  const selectedFeatures = useAppSelector(selectSelectedFeatures);
  const layers = useAppSelector((state) => state.layers.layers);
  const activeLayerIdFromLayers = useAppSelector((state) => state.layers.activeLayerId);

  // Get the active layer info
  const activeLayer = layers.find((l) => l.id === activeLayerIdFromLayers);
  const saveInFlightRef = useRef(false);

  // ─── Keyboard shortcuts for copy/paste ───────────────────────────────────

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+C: Copy selected features
      if (e.ctrlKey && e.key === 'c' && selectedFeatures.length > 0) {
        handleCopy();
      }
      // Ctrl+V: Paste features
      if (e.ctrlKey && e.key === 'v' && clipboard.length > 0 && activeLayer) {
        handlePaste();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedFeatures, clipboard, activeLayer]);

  // ─── Layer Creation ──────────────────────────────────────────────────────

  const handleOpenCreateDialog = useCallback(() => {
    dispatch(setCreateLayerDialogOpen(true));
  }, [dispatch]);

  const handleCloseCreateDialog = useCallback(() => {
    dispatch(setCreateLayerDialogOpen(false));
  }, [dispatch]);

  const handleCreateLayer = useCallback(
    (config: CreateLayerConfig) => {
      dispatch(
        createLayerAsync({
          name: config.name,
          geometryType: config.geometryType,
          crs: config.crs,
          attributeFields: config.attributeFields,
        })
      );
      dispatch(setCreateLayerDialogOpen(false));
    },
    [dispatch]
  );

  // ─── Digitization ───────────────────────────────────────────────────────

  const handleStartDigitization = useCallback(() => {
    if (!activeLayer || !activeLayer.geometryType) {
      dispatch(setDigitizationError('Select a layer with a geometry type to digitize'));
      return;
    }

    const geometryType = activeLayer.geometryType as LayerGeometryType;
    const attributeFields = activeLayer.attributeFields || getDefaultAttributeFields(geometryType);

    dispatch(setMeasurementMode(null));
    dispatch(setDrawingTool(null));
    dispatch(deactivateSplitTool());
    dispatch(deactivateNodeEditing());
    dispatch(
      startDigitization({
        layerId: activeLayer.id,
        geometryType,
        attributeFields,
      })
    );
  }, [activeLayer, dispatch]);

  const handleStopDigitization = useCallback(() => {
    dispatch(stopDigitization());
  }, [dispatch]);

  const handleAttributeConfirm = useCallback(
    async (attributes: Record<string, any>) => {
      if (saveInFlightRef.current) return;
      if (!activeDigitizationLayerId || !pendingGeometry) {
        dispatch(setDigitizationError('The digitization target is no longer available'));
        return;
      }

      saveInFlightRef.current = true;
      try {
        await dispatch(
          addFeatureAsync({
            layerId: activeDigitizationLayerId,
            geometry: pendingGeometry,
            properties: attributes,
          })
        ).unwrap();
        dispatch(clearPendingGeometry());
      } catch (error) {
        dispatch(
          setDigitizationError(
            error instanceof Error ? error.message : String(error || 'Failed to save feature')
          )
        );
      } finally {
        saveInFlightRef.current = false;
      }
    },
    [activeDigitizationLayerId, pendingGeometry, dispatch]
  );

  const handleAttributeCancel = useCallback(() => {
    dispatch(clearPendingGeometry());
  }, [dispatch]);

  // ─── Snapping ────────────────────────────────────────────────────────────

  const handleOpenSnappingConfig = useCallback(() => {
    dispatch(setSnappingConfigDialogOpen(true));
  }, [dispatch]);

  // ─── Copy/Paste ──────────────────────────────────────────────────────────

  const handleCopy = useCallback(() => {
    if (selectedFeatures.length === 0) return;

    const clipboardFeatures: ClipboardFeature[] = [];

    for (const selected of selectedFeatures) {
      const layer = layers.find((l) => l.id === selected.layerId);
      if (!layer?.features) continue;

      const feature = layer.features.find((f) => f.id === selected.featureId);
      if (!feature) continue;

      clipboardFeatures.push({
        geometry: feature.geometry,
        properties: feature.properties || {},
        sourceLayerId: layer.id,
        sourceGeometryType: (layer.geometryType as LayerGeometryType) || 'Point',
      });
    }

    if (clipboardFeatures.length > 0) {
      dispatch(copyToClipboard(clipboardFeatures));
      layerCreatorService.copyFeatures(
        clipboardFeatures.map((f) => ({ geometry: f.geometry, properties: f.properties })),
        clipboardFeatures[0].sourceLayerId,
        clipboardFeatures[0].sourceGeometryType
      );
    }
  }, [selectedFeatures, layers, dispatch]);

  const handlePaste = useCallback(() => {
    if (clipboard.length === 0 || !activeLayer || !activeLayer.geometryType) return;

    const targetGeometryType = activeLayer.geometryType as LayerGeometryType;

    // Get pasteable features with attribute mapping
    const pasteableFeatures = layerCreatorService.getPasteableFeatures(
      targetGeometryType,
      [] // Target fields - empty means copy all properties as-is
    );

    // Add each feature to the target layer
    for (const feature of pasteableFeatures) {
      dispatch(
        addFeatureAsync({
          layerId: activeLayer.id,
          geometry: feature.geometry,
          properties: feature.properties,
        })
      );
    }

    if (pasteableFeatures.length === 0) {
      dispatch(
        setDigitizationError(
          `Cannot paste: incompatible geometry types for target layer "${activeLayer.name}"`
        )
      );
    }
  }, [clipboard, activeLayer, dispatch]);

  // ─── Render ──────────────────────────────────────────────────────────────

  const isDigitizing = mode === 'drawing';

  const toolBtn = useCallback(
    (
      title: string,
      icon: React.ReactNode,
      onClick: () => void,
      active = false,
      disabled = false,
      color: 'primary' | 'secondary' | 'error' | 'success' | 'warning' | 'info' = 'primary'
    ) => (
      <Tooltip title={title} placement="bottom" arrow key={title}>
        <span>
          <IconButton
            size="small"
            onClick={onClick}
            disabled={disabled}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              color: active ? theme.palette[color].main : theme.palette.text.secondary,
              backgroundColor: active ? alpha(theme.palette[color].main, 0.1) : 'transparent',
              '&:hover': {
                backgroundColor: alpha(theme.palette.action.hover, 0.1),
                color: theme.palette.text.primary,
              },
              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
              },
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 20 } })}
          </IconButton>
        </span>
      </Tooltip>
    ),
    [theme]
  );

  return (
    <>
      {/* Create Layer */}
      {toolBtn('Create New Layer', <AddIcon />, handleOpenCreateDialog)}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Digitize */}
      {!isDigitizing
        ? toolBtn(
            activeLayer ? 'Start Digitizing' : 'Select a layer to digitize',
            <EditIcon />,
            handleStartDigitization,
            false,
            !activeLayer
          )
        : toolBtn('Stop Digitizing', <StopIcon />, handleStopDigitization, true, false, 'error')}

      {/* Snapping */}
      {toolBtn(
        'Snapping Settings',
        <GridOnIcon />,
        handleOpenSnappingConfig,
        snapping.enabled,
        false,
        'info'
      )}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Copy */}
      {toolBtn(
        'Copy Features (Ctrl+C)',
        <ContentCopyIcon />,
        handleCopy,
        false,
        selectedFeatures.length === 0
      )}

      {/* Paste */}
      {toolBtn(
        'Paste Features (Ctrl+V)',
        <ContentPasteIcon />,
        handlePaste,
        false,
        clipboard.length === 0 || !activeLayer
      )}

      {/* Clipboard indicator */}
      {clipboard.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <Chip
            label={`${clipboard.length} copied`}
            size="small"
            variant="outlined"
            sx={{
              backgroundColor: alpha(theme.palette.info.main, 0.05),
              color: theme.palette.info.main,
              fontSize: '0.65rem',
              height: 22,
              fontWeight: 600,
              borderColor: alpha(theme.palette.info.main, 0.2),
            }}
          />
        </Box>
      )}

      {/* Mode indicator */}
      {isDigitizing && (
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          <Chip
            label={`Drawing ${activeGeometryType}`}
            size="small"
            variant="filled"
            color="success"
            sx={{
              fontSize: '0.65rem',
              height: 22,
              fontWeight: 600,
              boxShadow: theme.shadows[2],
            }}
          />
        </Box>
      )}

      {/* Dialogs */}
      <CreateLayerDialog
        open={createLayerDialogOpen}
        onClose={handleCloseCreateDialog}
        onConfirm={handleCreateLayer}
      />

      <AttributeInputDialog
        open={attributeInputDialogOpen}
        onClose={handleAttributeCancel}
        onConfirm={handleAttributeConfirm}
        fields={activeAttributeFields}
        title="Enter Feature Attributes"
      />

      <SnappingConfigPanel />
    </>
  );
};

export default DigitizationToolbar;
